import { Edge, Node } from "@xyflow/react";
import { v4 as uuid } from "uuid";
import {
  ConditionNode,
  InternalNode,
  LeafNode,
  LogicalOperator,
  Outcome,
  PaneType,
  Representation,
} from "@unlikelyai-magic/shared/data-access/api";
import { UnderstandingRepresentation } from "./types";

/**
 * Convert API ConditionNode (from DecisionTree.rootNode) to Representation for convertToRF.
 * Handles both camelCase and snake_case so trees from APIs that return either format
 * (e.g. LayoutFlowStatic checks sub_conditions) are fully expanded; avoids silently dropping children.
 * Used by LayoutFlow, LayoutFlowStatic, and EditableTreeFlow.
 */
export function conditionNodeToRepresentation(
  node: ConditionNode | Record<string, unknown>,
  nodeName?: string
): Representation {
  const n = node as ConditionNode;
  const nodeAny = node as Record<string, unknown>;
  const subConditionsRaw =
    (n as InternalNode).subConditions ??
    nodeAny.sub_conditions ??
    nodeAny.children;
  const subConditions = Array.isArray(subConditionsRaw)
    ? subConditionsRaw
    : undefined;
  return {
    name: n.name ?? (nodeAny.name as string) ?? "",
    question:
      nodeName ?? n.question ?? (nodeAny.question as string) ?? n.name ?? "",
    logicalOperator:
      (n.logicalOperator as LogicalOperator) ??
      (nodeAny.logical_operator as LogicalOperator) ??
      LogicalOperator.AND,
    subConditions:
      subConditions?.map((sub) => conditionNodeToRepresentation(sub)) ?? [],
    propagateWhen:
      n.propagateWhen ??
      (nodeAny.propagate_when as Representation["propagateWhen"]),
    isInternallyEvaluated:
      (n as LeafNode)?.isInternallyEvaluated ??
      (nodeAny.is_internally_evaluated as boolean),
  };
}

export type RFNodeData = Record<string, unknown> & {
  productTreeId: string;
  label: string;
  value: Outcome;
  question: string;
  logicalOperator: LogicalOperator;
  propagateWhen?: Outcome[] | string[];
  isInternallyEvaluated?: boolean;
};

export type BlurbRFNodeData = Record<string, unknown> & {
  productTreeId: string;
  type: PaneType;
  typeExplanation: string;
  blurbInformation: string;
};

export const convertToBlurbRF = (
  productTreeId: string,
  type: PaneType,
  typeExplanation: string,
  blurbInformation: string
): { nodes: Node<BlurbRFNodeData>[]; edges: Edge[] } => {
  const id = uuid();
  const rfNode: Node<BlurbRFNodeData> = {
    id,
    data: {
      productTreeId,
      type,
      typeExplanation,
      blurbInformation,
    },
    type: "blurb",
    position: { x: 0, y: 0 },
    parentId: productTreeId,
  };
  return {
    nodes: [rfNode],
    edges: [],
  };
};

export const convertToRF = (
  productTreeId: string,
  representation: Representation,
  source?: string
): { nodes: Node<RFNodeData>[]; edges: Edge[] } => {
  const subConditions = representation.subConditions;
  const nodeType = subConditions?.length ? "connective" : "condition";

  // Read extra fields that may be passed through from the backend
  const repAny = representation as any;
  const propagateWhen =
    repAny.propagateWhen ?? repAny.propagate_when ?? undefined;
  const isInternallyEvaluated =
    repAny.isInternallyEvaluated ?? repAny.is_internally_evaluated ?? false;

  const id = uuid();
  const rfNode: Node<RFNodeData> = {
    id,
    data: {
      productTreeId,
      label: representation.name,
      value: Outcome.UNDECIDED,
      question: representation.question,
      logicalOperator: representation.logicalOperator,
      propagateWhen,
      isInternallyEvaluated,
    },
    type: nodeType,
    position: { x: 0, y: 0 },
    parentId: productTreeId,
    // extent: "parent",
  };

  const result = (subConditions ?? []).reduce<{
    nodes: Node<RFNodeData>[];
    edges: Edge[];
  }>(
    (acc, condition) => {
      const result = convertToRF(productTreeId, condition, id);
      return {
        nodes: [...acc.nodes, ...result.nodes],
        edges: [...acc.edges, ...result.edges],
      };
    },
    {
      nodes: [rfNode],
      edges: source
        ? [
            {
              id: `${source}-${id}`,
              type: "custom-edge",
              source,
              target: id,
              animated: true,
            },
          ]
        : [],
    }
  );

  return {
    nodes: [...result.nodes],
    edges: [...result.edges],
  };
};

const buildUnderstandingRepresentationForNodeId = (
  nodeId: string,
  nodes: Node<RFNodeData>[],
  edges: Edge[]
): any | undefined => {
  const node = nodes.find(({ id }) => id === nodeId);
  if (!node) return;

  const { id, data } = node;

  const childNodes = edges.reduce<UnderstandingRepresentation[]>(
    (representations, { source, target }) => {
      if (source !== id) return representations;
      const childNodeRepresentation = buildUnderstandingRepresentationForNodeId(
        target,
        nodes,
        edges
      );
      return childNodeRepresentation
        ? [...representations, childNodeRepresentation]
        : representations;
    },
    []
  );

  const { label, question, line } = data as any;

  return {
    name: label,
    question,
    line,
    ...(childNodes.length ? { subConditions: childNodes } : {}),
  };
};

/**
 * Build a ConditionNode (API shape) from a single root node id in the given nodes/edges.
 * Includes logicalOperator, propagateWhen, and isInternallyEvaluated for persistence.
 * Skips nodes already visited to avoid infinite recursion on cyclic edges.
 */
export const rfNodesEdgesToConditionNode = (
  rootNodeId: string,
  nodes: Node<RFNodeData>[],
  edges: Edge[]
): Record<string, unknown> | undefined => {
  const visited = new Set<string>();

  function build(nodeId: string): Record<string, unknown> | undefined {
    if (visited.has(nodeId)) return undefined;
    visited.add(nodeId);

    const node = nodes.find(({ id }) => id === nodeId);
    if (!node) return undefined;

    const data = node.data as RFNodeData;
    const childIds = edges
      .filter((e) => e.source === node.id)
      .map((e) => e.target);

    const subConditions: Record<string, unknown>[] = [];
    for (const childId of childIds) {
      const child = build(childId);
      if (child) subConditions.push(child);
    }

    const out: Record<string, unknown> = {
      name: data.label ?? data.question ?? "",
      question: data.question ?? data.label ?? "",
      logicalOperator: data.logicalOperator ?? "AND",
      sourceRules: [],
      ...(data.propagateWhen != null
        ? { propagateWhen: data.propagateWhen }
        : {}),
      ...(data.isInternallyEvaluated != null
        ? { isInternallyEvaluated: data.isInternallyEvaluated }
        : {}),
    };
    if (subConditions.length > 0) {
      out.subConditions = subConditions;
    } else {
      out.value = data.value ?? "UNDECIDED";
    }
    return out;
  }

  return build(rootNodeId);
};

export const convertToUnderstandingRepresentation = (
  nodes: Node<RFNodeData>[],
  edges: Edge[]
) =>
  nodes.reduce<any[]>((representations, { id }) => {
    // filter out non-root nodes
    if (edges.some(({ target }) => target === id)) return representations;

    const rootRepresentation = buildUnderstandingRepresentationForNodeId(
      id,
      nodes,
      edges
    );

    return rootRepresentation
      ? [...representations, rootRepresentation]
      : representations;
  }, []);

export const evaluateAnd = (input_values: Outcome[]): Outcome => {
  if (input_values.every((value) => value === Outcome.TRUE)) {
    return Outcome.TRUE;
  }
  if (input_values.some((value) => value === Outcome.FALSE)) {
    return Outcome.FALSE;
  }
  if (input_values.some((value) => value === Outcome.UNKNOWN)) {
    return Outcome.UNKNOWN;
  }
  return Outcome.UNDECIDED;
};

export const evaluateOr = (input_values: Outcome[]): Outcome => {
  if (input_values.some((value) => value === Outcome.TRUE)) {
    return Outcome.TRUE;
  }
  if (input_values.every((value) => value === Outcome.FALSE)) {
    return Outcome.FALSE;
  }
  if (input_values.some((value) => value === Outcome.UNKNOWN)) {
    return Outcome.UNKNOWN;
  }
  return Outcome.UNDECIDED;
};

export const evaluateNot = (input_values: Outcome[]): Outcome => {
  if (input_values.length !== 1) {
    throw new Error("NOT node only takes one input.");
  }
  const input_outcome = input_values[0];
  if (input_outcome === Outcome.UNDECIDED) {
    return Outcome.UNDECIDED;
  }
  if (input_outcome === Outcome.UNKNOWN) {
    return Outcome.UNKNOWN;
  }
  return input_outcome === Outcome.TRUE ? Outcome.FALSE : Outcome.TRUE;
};
